<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, type PropType } from 'vue';
import Textarea from 'primevue/textarea';
import RadioButton from 'primevue/radiobutton';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { type MatrixTableRow, type MatrixColumn, MatrixColumnType, DateMode,
  type MatrixFieldUploadedFile, type FormField, BlobModel } from '@/models';
import { getLocalTimezoneDetails, jsToPrimeVueDateFormat } from '@/utils';
import { useI18n } from 'vue-i18n';
import InputNumber from 'primevue/inputnumber';
import  FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload';
import { useEntryStore } from '@/stores/entry';
import CustomButton from '@/components/common/CustomButton.vue';
import { ButtonType } from '@/components/common';
import EntryItemNumberRange from './EntryItemNumberRange.vue';

const { t: $t } = useI18n();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  rowData: {
    type: Object as PropType<MatrixTableRow>,
    required: true
  },
  column: {
    type: Object as PropType<MatrixColumn>,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  previewMode: {
    type: Boolean
  },
  allRadio: {
    type: Boolean
  }
});

const entryStore = useEntryStore();
const rowData = ref(props.rowData);

const emit = defineEmits(['update:rowData', 'onAddDynamicRow', 'onDeleteDynamicRow', 'onRadioChangeListener']);

const onChangeListener = () => {
  emit('update:rowData', rowData.value);
};

const onAddDynamicRow = () => {
  emit('onAddDynamicRow', rowData.value);
};

const onDeleteDynamicRow = () => {
  emit('onDeleteDynamicRow', rowData.value);
};

const onRadioChangeListener = (index: number) => {
  emit('onRadioChangeListener', rowData.value, index);
};

const onInputChanges = () => {
  
  rowData.value.columnValues![props.index - 1].timezoneRecorded = getLocalTimezoneDetails();

  rowData.value.columnValues![props.index - 1].dateValue?.setSeconds(0);
  rowData.value.columnValues![props.index - 1].dateValue?.setMilliseconds(0);

  onChangeListener();
};

const dateFormat = computed(() => {
  if (!props.previewMode) {
    if (rowData.value.columnValues![props.index - 1].options!.mode === DateMode.DATE_TIME &&
      rowData.value.columnValues![props.index - 1].options!.format) {
      if (rowData.value.columnValues![props.index - 1].options!.timeFormat == 24) {
        return !rowData.value.columnValues![props.index - 1].options!.format?.toLowerCase().includes('hh:mm') ?
          `${rowData.value.columnValues![props.index - 1].options!.format} HH:mm` : '';
      }
      return !rowData.value.columnValues![props.index - 1].options!.format?.toLowerCase().includes('hh:mm') ?
        `${rowData.value.columnValues![props.index - 1].options!.format} hh:mm a` : '';
    } else if (rowData.value.columnValues![props.index - 1].options!.mode === DateMode.DATE &&
      rowData.value.columnValues![props.index - 1].options!.format) {
      return rowData.value.columnValues![props.index - 1].options!.format;
    }
  }
  return '';
});

const maxDate = ref();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let intervalId: any = null;
onMounted(async () => {
  if (rowData.value.columnValues && rowData.value.columnValues[props.index - 1] &&
    rowData.value.columnValues![props.index - 1].options?.dateConstraints?.max == 'now') {
    maxDate.value = new Date();
    intervalId = setInterval(() => {
      maxDate.value = new Date();
    }, 60000);
  }
});

onUnmounted(() => {
  clearInterval(intervalId);
});

const disabled = computed(() => {
  if (props.previewMode) {
    return true;
  }
  if (rowData.value.columnValues![props.index - 1].unShiftedDate) {
    return true;
  }
  return false;
});

const selectedDate = computed({
  get: () => rowData.value.columnValues![props.index - 1].unShiftedDate ?? rowData.value.columnValues![props.index - 1].dateValue,
  set: val => {
    rowData.value.columnValues![props.index - 1].dateValue = val;
  }
});

const customFileUploader = async (event: FileUploadUploaderEvent, column: MatrixColumn) => {
  const files = event.files as File[];
  let selectedFieldFiles : MatrixFieldUploadedFile | undefined = entryStore.selectedMatrixFieldFiles.find(file => 
    file.identifier === props.field.identifier && props.rowData.rowId === file.rowId && `${column.id}` === file.columnId);

  if (!selectedFieldFiles) {
    selectedFieldFiles = {
      identifier: props.field.identifier,
      rowId: props.rowData.rowId,
      columnId: `${column.id}`,
      files: []
    };
    entryStore.selectedMatrixFieldFiles.push(selectedFieldFiles);
  }

  files.forEach(file => {
    selectedFieldFiles.files.push({
      name: file.name,
      contentType: file.type,
      identifier: '',
      size: file.size,
      file,
      progress: 0
    });
  });
  await entryStore.getSignedUrlForMatrixFiles(selectedFieldFiles);
  onChangeListener();
};

const files = computed(() => {
  const selectedFieldFiles = entryStore.selectedMatrixFieldFiles.find(file => 
    file.identifier === props.field.identifier && props.rowData.rowId === file.rowId && `${props.column.id}` === file.columnId);
  return selectedFieldFiles ? selectedFieldFiles.files : [];
});

const downloadFile = (file: BlobModel) => {
  entryStore.downloadBlob(file.identifier, file.name);
};

const deleteFile = (file: BlobModel) => {
  const selectedFieldFiles = entryStore.selectedMatrixFieldFiles.find(selectedFile => 
    selectedFile.identifier === props.field.identifier && props.rowData.rowId === selectedFile.rowId &&
    `${props.column.id}` === selectedFile.columnId);
  if (selectedFieldFiles) {
    entryStore.deleteMatrixFieldFile(file.identifier, selectedFieldFiles);
  }
};
</script>

<template>
    <div class="cell-root">
      <div :class="`${rowData.rowHeader.length == 0 ? 'empty-row-header' : 'row-header'}`" v-if="index === 0">
        <div>{{ rowData.rowHeader }}</div>
        <Button icon="pi pi-plus"
          outlined
          v-if="rowData.dynamicRowConfig && !previewMode"
          @click="onAddDynamicRow"
          class="button"
          v-ripple />
        <Button icon="pi pi-times"
          outlined
          v-if="rowData.enableClear && !previewMode"
          @click="onDeleteDynamicRow"
          class="button clear"
          v-ripple />
      </div>

      <div :class="`field ${column.type === MatrixColumnType.SINGLE_CHOICE || column.type === MatrixColumnType.MULTIPLE_CHOICE ? 'check' : ''}
        ${allRadio ? 'all-radio' : ''}`"
        v-if="index !== 0">
        <Textarea autoResize
          v-model="rowData.columnValues![index - 1].textValue"
          v-if="column.type === MatrixColumnType.TEXT"
          :disabled="column.disable || previewMode"
          :placeholder="column.options?.placeholder"
          rows="1"
          @input="onChangeListener"
          class="text-input"/>
        <InputNumber id="input"
          v-model.number="rowData.columnValues![index - 1].numberValue"
          v-if="column.type === MatrixColumnType.NUMBER && !column.options?.range"
          @input="onChangeListener"
          mode="decimal"
          :maxFractionDigits="20"
          fluid
          :placeholder="column.options?.placeholder"
          :disabled="column.disable || previewMode"/>

        <div class="range-input">
          <EntryItemNumberRange v-if="column.type === MatrixColumnType.NUMBER && column.options?.range"
            v-model="rowData.columnValues![index - 1].textValue"
            :column="rowData.columnValues![index - 1]"
            :range="column.options?.range"
            :previewMode="previewMode" />
          <FontAwesomeIcon :icon="['far', 'circle-exclamation']"
            v-if="rowData.columnValues![index - 1].warn"
            color="red"
            size="lg"
            v-tooltip="rowData.columnValues![index - 1].warn"/>
        </div>

        <div class="radio" v-if="column.type === MatrixColumnType.SINGLE_CHOICE">
          <RadioButton v-model.boolean="rowData.columnValues![index - 1].booleanValue"
            :disabled="previewMode"
            @change="() => { onRadioChangeListener(index - 1); onChangeListener(); }"
            :value="true" />
        </div>

        <div class="checkbox" v-if="column.type === MatrixColumnType.MULTIPLE_CHOICE">
          <Checkbox v-model="rowData.columnValues![index - 1].booleanValue"
            :binary="true"
            :disabled="previewMode"
            @change="onChangeListener"/>
        </div>

        <div class="date-time" v-if="column.type === MatrixColumnType.DATE">
          <DatePicker v-model="selectedDate"
            class="date-picker"
            :timeOnly="column.options!.mode === DateMode.TIME"
            :showTime="column.options!.mode === DateMode.DATE_TIME"
            showIcon
            @dateSelect="onInputChanges"
            @change="onInputChanges"
            :dateFormat="jsToPrimeVueDateFormat(rowData.columnValues![index - 1].options!.format)"
            :placeholder="dateFormat"
            :hour-format="rowData.columnValues![index - 1].options!.timeFormat == 12 ? '12' : '24'"
            :disabled="disabled"
            :maxDate="maxDate"
            iconDisplay="input"
            fluid >
            <template #inputicon="slotProps">
              <i :class="`pi ${
                  column.options!.mode === DateMode.TIME ? 'pi-clock' :
                  column.options!.mode === DateMode.DATE_TIME ? 'pi-calendar-clock': 'pi-calendar'}`
                "
                @click="slotProps.clickCallback" />
            </template>
          </DatePicker>
        </div>

        <div class="select" v-if="column.type === MatrixColumnType.DROPDOWN">
          <Select v-model="rowData.columnValues![index - 1].choiceValue"
            :placeholder="$t('select')"
            optionLabel="label"
            v-if="!column.options!.multipleChoice"
            :disabled="previewMode || disabled"
            :options="column.options!.choices?.filter(choice => !choice.archived)"
            showClear
            filter
            @change="onChangeListener"
            class="select-input"/>
          <MultiSelect v-model="rowData.columnValues![index - 1].choiceValue"
            :options="column.options!.choices?.filter(choice => !choice.archived)"
            :placeholder="$t('select')"
            display="chip"
            v-if="column.options!.multipleChoice"
            :disabled="previewMode"
            filter
            @change="onChangeListener"
            optionLabel="label"
            class="select-input"/>
        </div>

        <div class="file-upload-container" v-if="column.type === MatrixColumnType.FILE">
          <FileUpload @uploader="(e) => customFileUploader(e, column)"
            :multiple="true"
            :fileLimit="5"
            :auto="true"
            :custom-upload="true"
            :showUploadButton="false"
            :showCancelButton="false"
            mode="basic"
            v-if="!previewMode" />
          <div class="files">
            <div class="file-item" v-for="file in files" :key="file.identifier">
              <div class="file-label">
                {{ file.name }}
              </div>
              <div v-if="file.progress"
                class="progress-container">
                <ProgressSpinner style="width: 32px" strokeWidth="4" />
              </div>
              <CustomButton :type="ButtonType.ICON_ONLY_OUTLINED"
                :icon="{style: 'far', icon: 'download'}"
                @onClick="() => downloadFile(file)"
                v-if="!file.progress && file.storedAt"/>
              <CustomButton :type="ButtonType.ICON_ONLY_OUTLINED"
                :icon="{style: 'fal', icon: 'trash'}"
                :marginLeft="8"
                @onClick="() => deleteFile(file)"
                v-if="!previewMode && !file.progress && file.storedAt"/>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<style scoped lang="scss">
.row-header {
  min-width: 150px;
  max-width: 200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;

  .button {
    border-color: rgba(0, 0, 0, 0.25);
  }

  .clear {
    color: #ea3546;
  }
}
.empty-row-header {
  min-width: 40px;
  background-color: white;
}
.field {
  min-width: 100px;
  display: flex;
  justify-content: start;
}
.check {
  align-items: center;
  justify-content: center;
}
.all-radio {
  min-width: 20px;
}
.date-picker {
  min-width: 200px;
}
.select-input {
  width: 200px;
}
:deep(.p-multiselect-label) {
  overflow: auto;
}
.file-upload-container {
  display: flex;
  gap: 8px
}
.progress-container {
  margin-right: 8px;
}
:deep(.p-progressspinner) {
  height: 32px;
}
:deep(.p-button-label) {
  display: none;
}
.files {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.file-item {
  display: flex;
  align-items: center;
}
.file-label {
  width: 100px;
  color: #272a2e;
  overflow: hidden;
  white-space: nowrap;
  font-weight: 400;
  text-overflow: ellipsis;
}
.icon-button {
  margin-left: 8px;
  width: 24px;
  height: 24px;
  border: #272a2e 1px solid;
}
.range-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
</style>
